import { compositeLinearLightPixels, loadLinearLightImage } from './source';

// Mirror CombinedBlurFilter for SVG-as-image thumbnail/AMP captures, where
// Chromium omits backdrop filters. The live canvas continues sampling the page.
export async function compositeCombinedBlur(
  backdrop: HTMLCanvasElement,
  source: HTMLImageElement,
  maskUri: string,
  bounds: { x: number; y: number; width: number; height: number },
  blur: number,
  blendMode: string,
  opacity: number,
  feather = 0,
  paintMaskUri?: string
) {
  // Retain authored off-page paint/masks until every filter has run. Drawing
  // them into a page-sized canvas first creates a false feathered page edge.
  const left = Math.floor(Math.min(0, bounds.x));
  const top = Math.floor(Math.min(0, bounds.y));
  const width =
    Math.ceil(Math.max(backdrop.width, bounds.x + bounds.width)) - left;
  const height =
    Math.ceil(Math.max(backdrop.height, bounds.y + bounds.height)) - top;
  const makeCanvas = () => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  };
  const paddedBackdrop =
    left === 0 &&
    top === 0 &&
    width === backdrop.width &&
    height === backdrop.height
      ? backdrop
      : makeCanvas();
  const paddedSource = makeCanvas();
  const background = paddedBackdrop.getContext('2d');
  const foreground = paddedSource.getContext('2d');
  const target = backdrop.getContext('2d');
  if (!background || !foreground || !target)
    throw new Error('Blur capture requires a canvas context.');
  if (paddedBackdrop !== backdrop) background.drawImage(backdrop, -left, -top);
  foreground.drawImage(
    source,
    bounds.x - left,
    bounds.y - top,
    bounds.width,
    bounds.height
  );
  await compositePaddedBlur(
    paddedBackdrop,
    paddedSource,
    maskUri,
    { ...bounds, x: bounds.x - left, y: bounds.y - top },
    blur,
    blendMode,
    opacity,
    feather,
    paintMaskUri
  );
  // Replace with the completed page, rather than alpha-compositing it twice.
  if (paddedBackdrop !== backdrop) {
    target.clearRect(0, 0, backdrop.width, backdrop.height);
    target.drawImage(paddedBackdrop, left, top);
  }
}

async function compositePaddedBlur(
  backdrop: HTMLCanvasElement,
  source: HTMLCanvasElement,
  maskUri: string,
  bounds: { x: number; y: number; width: number; height: number },
  blur: number,
  blendMode: string,
  opacity: number,
  feather = 0,
  paintMaskUri?: string
) {
  const makeCanvas = () => {
    const canvas = document.createElement('canvas');
    canvas.width = backdrop.width;
    canvas.height = backdrop.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Blur capture requires a canvas context.');
    return { canvas, context };
  };
  const blurCanvas = async (input: HTMLCanvasElement, radius: number) => {
    const result = makeCanvas();
    if (radius <= 0 || 'filter' in result.context) {
      result.context.filter = radius > 0 ? `blur(${radius}px)` : 'none';
      result.context.drawImage(input, 0, 0);
    } else {
      // WebKit versions without Canvas2D filters silently ignore assignment.
      // Ordinary SVG image filtering is supported there, unlike SVG backdrop
      // references. Keep the same padded filter bounds and sRGB math.
      const { width, height } = input;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"><defs><filter id="blur" filterUnits="userSpaceOnUse" x="0" y="0" width="${width}" height="${height}" color-interpolation-filters="sRGB"><feGaussianBlur stdDeviation="${radius}"/></filter></defs><image href="${input.toDataURL()}" width="${width}" height="${height}" filter="url(#blur)"/></svg>`;
      const image = await loadLinearLightImage(
        `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
      );
      result.context.drawImage(image, 0, 0);
    }
    return result;
  };
  const blurredBackdrop = await blurCanvas(backdrop, blur);
  const blurred = await blurCanvas(source, blur);
  const maskImage = await loadLinearLightImage(maskUri);
  const frame = makeCanvas();
  frame.context.drawImage(
    maskImage,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height
  );
  const paintFrameSource = paintMaskUri ? makeCanvas() : frame;
  if (paintMaskUri)
    paintFrameSource.context.drawImage(
      await loadLinearLightImage(paintMaskUri),
      bounds.x,
      bounds.y,
      bounds.width,
      bounds.height
    );
  const footprint =
    feather > 0
      ? await blurCanvas(paintFrameSource.canvas, feather)
      : paintFrameSource;
  if (paintMaskUri) {
    const inner = feather > 0 ? await blurCanvas(frame.canvas, feather) : frame;
    const ratio = inner.context.getImageData(
      0,
      0,
      backdrop.width,
      backdrop.height
    );
    const outer = footprint.context.getImageData(
      0,
      0,
      backdrop.width,
      backdrop.height
    );
    for (let i = 3; i < ratio.data.length; i += 4)
      ratio.data[i] = outer.data[i]
        ? Math.min(255, Math.round((255 * ratio.data[i]) / outer.data[i]))
        : 0;
    inner.context.putImageData(ratio, 0, 0);
    blurredBackdrop.context.globalCompositeOperation = 'destination-in';
    blurredBackdrop.context.drawImage(inner.canvas, 0, 0);
    blurredBackdrop.context.globalCompositeOperation = 'destination-over';
    blurredBackdrop.context.drawImage(backdrop, 0, 0);
  }
  let paint = blurred;
  if (feather > 0) {
    const feathered = await blurCanvas(paint.canvas, feather);
    feathered.context.filter = 'none';
    feathered.context.globalCompositeOperation = 'source-atop';
    feathered.context.drawImage(paint.canvas, 0, 0);
    paint = feathered;
  }
  // Match the paint's Blur/Softness order on the padded working surface.
  // Divide out geometric alpha; retain intrinsic image/Style alpha.
  const paintFrame = await blurCanvas(
    (
      await blurCanvas(paintFrameSource.canvas, blur)
    ).canvas,
    feather
  );
  const pixels = paint.context.getImageData(
    0,
    0,
    backdrop.width,
    backdrop.height
  );
  const coverage = paintFrame.context.getImageData(
    0,
    0,
    backdrop.width,
    backdrop.height
  );
  for (let i = 3; i < pixels.data.length; i += 4) {
    pixels.data[i] = coverage.data[i]
      ? Math.min(255, Math.round((255 * pixels.data[i]) / coverage.data[i]))
      : 0;
  }
  paint.context.putImageData(pixels, 0, 0);
  const result = makeCanvas();
  result.context.drawImage(blurredBackdrop.canvas, 0, 0);
  if (blendMode === 'linear-light') {
    const background = result.context.getImageData(
      0,
      0,
      backdrop.width,
      backdrop.height
    );
    const foreground = paint.context.getImageData(
      0,
      0,
      backdrop.width,
      backdrop.height
    );
    compositeLinearLightPixels(background.data, foreground.data, 1);
    result.context.putImageData(background, 0, 0);
  } else {
    result.context.globalCompositeOperation =
      blendMode === 'normal'
        ? 'source-over'
        : (blendMode as GlobalCompositeOperation);
    result.context.drawImage(paint.canvas, 0, 0);
  }
  result.context.globalCompositeOperation = 'destination-in';
  result.context.drawImage(footprint.canvas, 0, 0);
  const context = backdrop.getContext('2d');
  if (!context) throw new Error('Blur capture requires a canvas context.');
  context.save();
  context.globalAlpha = Math.min(1, Math.max(0, opacity));
  context.drawImage(result.canvas, 0, 0);
  context.restore();
}
